import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import CategoryModal from '../../../modals/category_modal/ui/Main';
import LocationModal from '../../../modals/location_modal/ui/Main';
import FilterModal from '../../../modals/filter_modal/ui/Main';

import filterIcon from '../../../../assets/icons/filter.svg';
import { t } from 'i18next';

const OpenModalButton = ({
    onSelect,
    type = 'category',
    selectedCategoryId,
    selectedSubcategoryId,
    selectedLocationId,
    placeholder,
    className = ''
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedText, setSelectedText] = useState(placeholder || (type === 'filter' ? t('filters') : t('select')));

    const updateSelectedText = useCallback(() => {
        if (type === 'filter') {
            setSelectedText(t('filters'));
            return;
        }
        if (type === 'location') {
            setSelectedText(t('select'));
            return;
        }
        const catName = localStorage.getItem('selectedCategoryName');
        const subName = localStorage.getItem('selectedSubcategoryName');
        if (catName && subName) {
            setSelectedText(`${catName} › ${subName}`);
        } else {
            setSelectedText(placeholder || t('select'));
        }
    }, [type, placeholder]);

    useEffect(() => {
        updateSelectedText();
    }, [updateSelectedText, selectedCategoryId, selectedSubcategoryId]);

    useEffect(() => {
        const handler = () => updateSelectedText();
        window.addEventListener('localStorageUpdated', handler);
        window.addEventListener('storage', handler);
        return () => {
            window.removeEventListener('localStorageUpdated', handler);
            window.removeEventListener('storage', handler);
        };
    }, [updateSelectedText]);

    const handleOpenModal = useCallback(() => {
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden';
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        document.body.style.overflow = '';
    }, []);

    const handleSelect = useCallback((selection) => {
        if (type === 'category') {
            const catName = localStorage.getItem('selectedCategoryName');
            const subName = localStorage.getItem('selectedSubcategoryName');
            setSelectedText(catName && subName ? `${catName} › ${subName}` : placeholder || t('select'));
        }
        if (onSelect) {
            onSelect({
                categoryId: selection.category,
                subcategoryId: selection.subcategory,
                filterOption: selection.filterOption,
                location: selection.location,
            });
        }
        handleCloseModal();
    }, [type, onSelect, handleCloseModal, placeholder]);

    useEffect(() => {
        if (!isModalOpen) return;
        const handleEscape = (e) => { if (e.key === 'Escape') handleCloseModal(); };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isModalOpen, handleCloseModal]);

    const hasValue = type === 'category'
        ? !!(localStorage.getItem('selectedCategoryName') && localStorage.getItem('selectedSubcategoryName'))
        : false;

    const renderModal = () => {
        if (!isModalOpen) return null;

        if (type === 'category') {
            return createPortal(
                <CategoryModal
                    onSelect={handleSelect}
                    onClose={handleCloseModal}
                    initialSelectedCategoryId={selectedCategoryId}
                    initialSelectedSubcategoryId={selectedSubcategoryId}
                />,
                document.body
            );
        }
        if (type === 'location') {
            return createPortal(
                <LocationModal menuVisible={true} onSelect={handleSelect} onClose={handleCloseModal} />,
                document.body
            );
        }
        if (type === 'filter') {
            return createPortal(
                <>
                    <div onClick={handleCloseModal} className="click navigation_content_background" />
                    <div className="navigation_content">
                        <FilterModal onSelect={handleSelect} onClose={handleCloseModal} />
                    </div>
                </>,
                document.body
            );
        }
        return null;
    };

    /* ── Filter button variant ── */
    if (type === 'filter') {
        return (
            <>
                <button
                    type="button"
                    onClick={handleOpenModal}
                    aria-label={t('filters')}
                    className={`md:hidden w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-[var(--primary-color)] bg-white text-gray-700 text-sm font-medium cursor-pointer transition-all hover:bg-[var(--primary-color)]/5 mb-2.5 ${className}`}
                >
                    <div className="flex items-center gap-2">
                        <img src={filterIcon} alt="filter" className="w-5 h-5 opacity-70" />
                        <span>{t('filters')}</span>
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
                {renderModal()}
            </>
        );
    }

    /* ── Category / Location button variant ── */
    return (
        <>
            <button
                type="button"
                onClick={handleOpenModal}
                aria-label={selectedText}
                aria-expanded={isModalOpen}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border transition-all duration-200 cursor-pointer text-sm
                    ${hasValue
                        ? 'bg-[var(--primary-color)]/8 border-[var(--primary-color)] text-[var(--primary-color)]'
                        : 'bg-white border-gray-300 text-gray-500 hover:border-[var(--primary-color)] hover:bg-[var(--primary-color)]/5'
                    } ${className}`}
            >
                {/* Left: category icon dot + text */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    {hasValue && (
                        <span className="w-2 h-2 rounded-full bg-[var(--primary-color)] flex-shrink-0" />
                    )}
                    <span className="truncate font-medium">{selectedText}</span>
                </div>

                {/* Right: clear or chevron */}
                {hasValue ? (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            localStorage.removeItem('selectedCategoryName');
                            localStorage.removeItem('selectedSubcategoryName');
                            localStorage.removeItem('selectedCategoryId');
                            localStorage.removeItem('selectedSubcategoryId');
                            setSelectedText(placeholder || t('select'));
                        }}
                        className="w-5 h-5 flex items-center justify-center rounded-full bg-[var(--primary-color)]/20 hover:bg-[var(--primary-color)]/40 flex-shrink-0 border-none cursor-pointer p-0 transition-colors"
                        aria-label="Тазалау"
                    >
                        <svg className="w-3 h-3 text-[var(--primary-color)]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                    </button>
                ) : (
                    <svg
                        className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${isModalOpen ? 'rotate-90' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                )}
            </button>
            {renderModal()}
        </>
    );
};

export default OpenModalButton;
